import { useState, useEffect } from 'react';

import { ApolloError } from '@apollo/client';
import { Button } from '@netfront/ui-library';
import { IProjectUsage } from 'interfaces';
import { useRouter } from 'next/router';
import { last } from 'utils';

import { ReportTable } from '../ReportTable';

import { TABLE_COLUMNS_TEXT_SPEECH } from './TranslateTableView.constants';
import { getProjectUsageTableData } from './TranslateTableView.helpers';
import { IProjectUsageTableData, TranslateTableViewProps } from './TranslateTableView.interfaces';

import { useExportUsageReport, useGetProjectUsage, useToast } from '../../../../../hooks';
import { REPORTING_PAGE_CONSTANTS } from '../../ReportingPage.constants';


const { pageSize: defaultPageSize, tableSelectionItems } = REPORTING_PAGE_CONSTANTS;

const TranslateTableView = ({
  selectedTable,
  onTableChange,
  dateRange,
  onDateRangeChange,
  selectedView,
  onViewChange,
}: TranslateTableViewProps) => {
  const { query } = useRouter();
  const { handleToastError } = useToast();

  const { projectId } = query;

  const [projectUsageTableData, setProjectUsageTableData] = useState<IProjectUsageTableData[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [lastCursor, setLastCursor] = useState<string>();
  const [allProjectUsages, setAllProjectUsages] = useState<IProjectUsage[]>([]);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);


  const handleApolloError = (error?: ApolloError) => {
    if (!error) {
      return;
    }

    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleExportUsageReport, isLoading: isExportUsageReportLoading = false } = useExportUsageReport({
    fetchPolicy: 'no-cache',
    onCompleted: ({ report: { presignedUrl } }) => {
      window.open(presignedUrl, '_blank');
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const { handleGetProjectUsage, handleFetchMoreProjectUsage, isLoading: isGetProjectUsageLoading = false } = useGetProjectUsage({
    fetchPolicy: 'no-cache',
    onCompleted: ({ edges, totalCount = 0 }) => {

      const lastEdge = last(edges);

      if (lastEdge && lastEdge.cursor !== lastCursor) {
        setLastCursor(lastEdge.cursor);
      }

      const projectUsages = edges.map(({ node }) => node);
      setAllProjectUsages(projectUsages);
      setIsLoadMoreDisabled(projectUsages.length >= totalCount || totalCount <= pageSize);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const handleChangeSearchValue = (value?: string) => {
    setFilter(value ?? '');
  };

  const handlePageSizeChange = (changedPageSize: number) => {
    setPageSize(changedPageSize);
  };

  const handleDownloadReport = () => {
    handleExportUsageReport({
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };

  const handleGenerateTranslateReport = () => {
    handleGetProjectUsage({
      filter,
      first: pageSize,
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };


  useEffect(() => {
    if (selectedTable !== tableSelectionItems[0].value) {
      return;
    }
    
    handleGenerateTranslateReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedTable, pageSize, filter]);

    useEffect(() => {
    setProjectUsageTableData(
      getProjectUsageTableData({
        projectUsages: allProjectUsages,
      }),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProjectUsages]);

  const isLoading = isExportUsageReportLoading || isGetProjectUsageLoading;

  return (
    <ReportTable<IProjectUsageTableData> 
      childrenEnd={<Button size="xs" text="Download" type="button" onClick={() => handleDownloadReport()} />}
      columns={TABLE_COLUMNS_TEXT_SPEECH}
      data={projectUsageTableData}
      dateRange={dateRange}
      emptyStateMessage="There are no project usage data available yet"
      handleOnPageSizeChange={handlePageSizeChange}
      handleOnPaginate={async () => {
        await handleFetchMoreProjectUsage({
          after: lastCursor,
          first: pageSize,
          filter,
          from: dateRange[0],
          ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          projectId: String(projectId),
          to: dateRange[1],
        });
      }}
      handleSearch={handleChangeSearchValue}
      isLoading={isLoading}
      isLoadMoreDisabled={isLoadMoreDisabled}
      pageSize={pageSize}
      selectedTable={selectedTable}
      selectedView={selectedView}
      totalItems={totalItems}
      onDateRangeChange={onDateRangeChange}
      onTableChange={onTableChange}
      onViewChange={onViewChange}
    />
  );
};

export { TranslateTableView };
